import { AdvancedSearchGroupType, AdvancedSearchNodeType } from "../config/enums";
import { SearchColumn, SearchNode } from "./advancedSearch.types";
import { getAdvancedSearchEntityName, getField, getLabelForJoinEntity } from "./searchFields";
import { SearchJoin } from "./searchEntities.types";

type Filters = { Key: string; Value: string }[];

// The saved shape of a rule, as the service stores it.
interface SavedNode {
  NodeType: number;
  Field?: string;
  Operator?: string;
  Value?: unknown;
  DateTimeRange?: unknown;
  GroupType?: number;
  Children?: SavedNode[];
}

type UnresolvedFields = string[];

function getAdvancedNodeValueFromSavedNode(node: SavedNode, field: SearchColumn) {
  if (field.type === "datetime") {
    const dates = String(node.Value).split("|");
    const part = (suffix: string, value: string) => ({
      Key: `${field.name}-${suffix}`,
      DateTimeRange: node.DateTimeRange,
      Value: value,
      Type: field.type,
      FilterAction: field.filterAction,
      SetInitially: true,
    });

    return [part("From", dates[0]), part("To", dates[1])];
  }

  if (field.type === "bit") {
    // Undo the inversion and int conversion applied on submit
    const bitValue = ["1", "true"].includes(String(node.Value).toLowerCase());

    return field.inverted ? !bitValue : bitValue;
  }

  if (["in", "notin"].includes(node.Operator as string)) {
    return String(node.Value).split("|");
  }

  return node.Value;
}

function getAdvancedNodeFromSavedNode(
  filters: Filters,
  joins: SearchJoin[],
  node: SavedNode,
  unresolvedFields: UnresolvedFields
): SearchNode | null {
  if (node.NodeType === 1) {
    return {
      children: (node.Children ?? [])
        .map((child) => getAdvancedNodeFromSavedNode(filters, joins, child, unresolvedFields))
        .filter((child) => child !== null),
      groupType: node.GroupType === 0 ? AdvancedSearchGroupType.And : AdvancedSearchGroupType.Or,
      nodeType: AdvancedSearchNodeType.Group,
    } as SearchNode;
  }

  const found = getField(filters, joins, node.Field as string);

  if (!found) {
    // Dropping the node would silently widen the search, so record it.
    unresolvedFields.push(node.Field as string);

    return null;
  }

  const { field, fieldName, join } = found;

  return {
    ...field,
    alias: join ? join.Alias : "",
    label: join ? getLabelForJoinEntity(join, field.label) : field.label,
    name: field.linkedEntityId ?? fieldName,
    nodeType: node.NodeType,
    operator: node.Operator,
    value: getAdvancedNodeValueFromSavedNode(node, field),
  } as unknown as SearchNode;
}

/**
 * Rebuild the rule tree for a saved view. Fields that couldn't be matched are collected in
 * `unresolvedFields`, so the caller can report that the view didn't load in full.
 */
export function getAdvancedNodesFromSavedNodes(
  filters: Filters,
  nodes: unknown[],
  table: { getJoins?: (filters: Filters) => SearchJoin[] },
  unresolvedFields: UnresolvedFields = []
): SearchNode[] {
  const joins = table.getJoins ? table.getJoins(filters) : [];

  return nodes
    .map((node) => getAdvancedNodeFromSavedNode(filters, joins, node as SavedNode, unresolvedFields))
    .filter((node): node is SearchNode => node !== null);
}

/** What stops a search being run: no entity, or a rule that needs a value and has none. */
export function validateSearch(filters: Filters, advancedNodes: unknown) {
  const errors: Record<string, string> = {};

  if (getAdvancedSearchEntityName(filters) === "") {
    errors.advancedSearchEntityName = "Required";
  }

  type Rule = { nodeType?: string; children?: Rule[]; operator?: string; value?: unknown };

  const validateNodes = (nodes: Rule[], path: number[] = []) => {
    nodes.forEach((node, index) => {
      const currentPath = [...path, index];

      if (node.nodeType === AdvancedSearchNodeType.Group) {
        validateNodes(node.children ?? [], currentPath);

        return;
      }

      if (node.nodeType !== AdvancedSearchNodeType.Filter) {
        return;
      }

      const needsValue = !["containsdata", "doesnotcontaindata"].includes(node.operator?.toLowerCase?.() ?? "");

      if (needsValue && (node.value === "" || node.value === null || node.value === undefined)) {
        errors[`advancedFilters${currentPath.join("-")}`] = "Required";
      }
    });
  };

  if (Array.isArray(advancedNodes)) {
    validateNodes(advancedNodes);
  }

  return errors;
}
